// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { TokenRange } from "networking/api";
import { BoundsComponent, createDefaultBoundsComponent } from "sdv/components/BoundsComponent";
import { ControlComponent, ControlType, postOrderTraversal } from "sdv/components/ControlComponent";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { ComponentId } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { rangeOverlapsPages, Page } from "sdv/Page";
import { transformBoxPageToWorldSpace } from "sdv/Space";
import { clampTokenRangeToPages, createBoundsForTokenRange, mergeBoxesIntoContainerBoxes, mergeGapsBetweenBoxes, sortEntitiesByTokenRangeAscending } from "sdv/utils/TokenBounds";
import { store } from "store";
import { dgmlSlice, selectChunkBySpanId } from "store/dgml/dgmlSlice";
import { Chunk } from "store/dgml/dgmlTypes";
import { Box, combineBoxes, TokenIdToBox } from "store/shared_types";
import { ISystem, Queries } from "../engine/Engine";
import { IResources, ResourceDependencies } from "../Resources";
import { getControlType } from "./ControlTreeSystem";

const shouldUpdateDeps: ResourceDependencies = ["rootControl", "pagesLoaded"];
const resourceDeps: ResourceDependencies = ["pageTokens", "visiblePages", "pages"];

/**
 * Calculates the bounds for all the visible controls in the system.
 */
export class ControlBoundsSystem implements ISystem {
    get updateEnabled() { return true; }

    resources!: IResources;
    entities!: IEntityManager

    queries: Queries = {
        rootControl: {
            types: [ComponentId.RootControl, ComponentId.Active]
        },
        tables: { types: [ComponentId.Table, ComponentId.Control, ComponentId.Bounds] },
        multiSelect: { types: [ComponentId.MultiSelect, ComponentId.TokenRange] }
    }

    update() {
        if (!this.resources.get("pagesLoaded").value) {
            return;
        }

        this.updateMultiSelectChunks();
        this.updateControlTree();
    }

    private updateControlTree() {
        if (!this.resources.check(shouldUpdateDeps)) {
            return;
        }

        const values = this.resources.values(resourceDeps);
        const rootEntity = this.queries.rootControl.query?.entities[0];
        if (!rootEntity) {
            return;
        }

        // Walk the tree from bottom up and create bounds for each control
        const iterator = postOrderTraversal(rootEntity, this.entities);
        let result = iterator.next();

        // Collect entities who are parents of structural chunks since we need to update
        // their bounds accordingly.
        const entitiesToUpdate: Entity[] = [];
        while (!!result.value) {
            const entity: Entity = result.value;
            const control = entity.getComponent<ControlComponent>(ComponentId.Control)!;
            const tokenRange = { fromTok: control.fromTok, toTok: control.toTok };
            const boundsComp = createBounds(control.type, control.node, tokenRange, values.pageTokens!, values.visiblePages!, values.pages!);
            if (!!boundsComp) {
                entity.ensureComponent(boundsComp, true);
            }
            else {
                entity.removeComponent(ComponentId.Bounds);
            }

            if (control.type === ControlType.Table) {
                let parentId: string | undefined = control.parent;
                while (!!parentId) {
                    const parentEntity = this.entities.get(parentId);
                    if (!!parentEntity) {
                        entitiesToUpdate.push(parentEntity);
                    }

                    parentId = parentEntity?.getComponent<ControlComponent>(ComponentId.Control)!.parent;
                }
            }

            result = iterator.next();
        }

        if (this.queries.tables.query!.any()) {
            const sortedEntities = [...this.queries.tables.query!.entities];
            sortEntitiesByTokenRangeAscending(sortedEntities);

            for (const entity of entitiesToUpdate) {
                const controlComp = entity.getComponent<ControlComponent>(ComponentId.Control)!;
                if (controlComp.type === ControlType.LargeChunk || controlComp.type === ControlType.SmallChunk) {
                    const boundsComp = entity.getComponent<BoundsComponent>(ComponentId.Bounds)!;
                    boundsComp.bounds = createControlTokenBoxBounds(controlComp, values.pageTokens!, values.pages!, false, sortedEntities)
                    boundsComp.containerBoxes = mergeBoxesIntoContainerBoxes(boundsComp.bounds)
                }
            }
        }
    }

    private updateMultiSelectChunks() {
        if (!this.resources.check(shouldUpdateDeps)) {
            return;
        }

        const documentId = this.resources.get("documentId").value;
        const folderId = createFolderId(documentId);
        const state = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: folderId.clusterId, fileId: folderId.fileId })(store.getState());

        if (!state.data?.root) {
            return [];
        }

        const values = this.resources.values(resourceDeps);
        const targetEntities: Entity[] = [];
        for (const multiSelectEntity of this.queries.multiSelect.query!.entities) {
            const tokenRange = multiSelectEntity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            if (rangeOverlapsPages(tokenRange, values.visiblePages!)) {
                targetEntities.push(multiSelectEntity);
            }
        }

        for (const entity of targetEntities) {
            let boundsComp = createDefaultBoundsComponent();
            const spanId = entity.getComponent<MultiSelectComponent>(ComponentId.MultiSelect)!.spanId;
            const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            const chunk = selectChunkBySpanId(state.data!.chunks, spanId);
            if (!!chunk) {
                const controlType = getControlType(documentId, chunk);
                boundsComp = createBounds(controlType, chunk, tokenRange, values.pageTokens!, values.visiblePages!, values.pages!) ?? boundsComp;
            }
            else {
                boundsComp.bounds = createTokenBoxBounds(tokenRange, values.pageTokens!, values.pages!, false)
                boundsComp.containerBoxes = mergeBoxesIntoContainerBoxes(boundsComp.bounds)
            }

            entity.ensureComponent(boundsComp, true);
        }
    }
}

function createBounds(controlType: ControlType, node: Chunk, tokenRange: TokenRange, tokens: TokenIdToBox, visiblePages: Page[], allPages: Page[]): BoundsComponent | null {
    const boundsComponent = createDefaultBoundsComponent();

    switch (controlType) {
        case ControlType.SmallChunk:
        case ControlType.LargeChunk:
            boundsComponent.collisionEnabled = true;
            boundsComponent.bounds = createTokenBoxBounds(tokenRange, tokens, visiblePages, true);
            boundsComponent.containerBoxes = mergeBoxesIntoContainerBoxes(boundsComponent.bounds)
            boundsComponent.boundingBox = combineBoxes(boundsComponent.bounds);
            break;

        case ControlType.Table:
        case ControlType.TableBody:
        case ControlType.TableCell:
        case ControlType.TableRow:
        case ControlType.Outflow:
            let boxes = node.style?.boundingBoxes?.map(b => {
                const page = allPages.find(x => x.pageIndex + 1 === b.page);
                return transformBoxPageToWorldSpace(page!, b);
            }) ?? [];

            boundsComponent.collisionEnabled = true;
            boundsComponent.bounds = boxes;
            boundsComponent.containerBoxes = boxes;
            boundsComponent.boundingBox = combineBoxes(boxes);

            break;

        default:
            return null;
    }

    return boundsComponent
}

function createControlTokenBoxBounds(c: ControlComponent, tokens: TokenIdToBox, pages: Page[], clamp: boolean, structuralEntities: Entity[] = []): Box[] {
    return createTokenBoxBounds({ fromTok: c.fromTok, toTok: c.toTok }, tokens, pages, clamp, structuralEntities);
}

function createTokenBoxBounds(tokenRange: TokenRange, tokens: TokenIdToBox, pages: Page[], clamp: boolean, structuralEntities: Entity[] = []): Box[] {
    const range = clamp ? clampTokenRangeToPages(tokenRange.fromTok, tokenRange.toTok, pages) : tokenRange;
    if (!range) {
        return [];
    }

    const bounds = createBoundsForTokenRange(range.fromTok, range.toTok, tokens, structuralEntities);
    mergeGapsBetweenBoxes(bounds, 10);

    return bounds;
}
